const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-02" - a date with no time component at all. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * "2026-10-02T18:30" / "2026-10-02 18:30" - the wall-clock time as authored.
 * Deliberately prefix-matches, so a value that also carries a zone designator
 * ("2026-10-02T18:30:00.000Z") is still read as the wall-clock 18:30.
 */
const WALL_CLOCK_DATETIME = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/;

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Formats an event date, and a time ONLY when the stored value really has one.
 *
 * Event dates are persisted date-only ("2026-10-02"); see
 * `EventDetails.eventDate` in eventi-backend/src/types/dynamodb.ts. Passing such
 * a value to `new Date()` makes JavaScript treat it as **UTC midnight**, so the
 * previous `getHours()`-based formatters printed an invented time on every row
 * (03:00 for Qatar, UTC+3) and even rolled the day backwards for anyone west of
 * UTC. That fabricated "03:00" is the hard-coded time this replaces.
 *
 * **The wall-clock time is rendered exactly as authored, and any trailing zone
 * designator is ignored.** The event's calendar date is zone-less - `eventDate`
 * is date-only and checkout rejects a value carrying a time - so the time the
 * customer typed alongside it is a local wall-clock reading too. The order's
 * `startDateTime` is stored with a spurious `Z`, but its date portion always
 * matches `eventDate`, so reading the wall-clock is what keeps the two in
 * agreement. Converting it instead shifted the time by the viewer's offset (a
 * vendor in Qatar read 22:16 for an 19:16 event) and could push the rendered
 * date across midnight, contradicting the `eventDate` shown next to it.
 *
 * Genuine instants - epoch milliseconds, which is what the order-created
 * timestamps are - still go through `Date`, since those have no authored
 * wall-clock to preserve.
 */
export const formatEventDateTime = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (DATE_ONLY.test(trimmed)) {
      const [year, month, day] = trimmed.split('-').map(Number);
      return `${day} ${MONTHS[month - 1]} ${year}`;
    }

    const wallClock = WALL_CLOCK_DATETIME.exec(trimmed);
    if (wallClock) {
      const [, datePart, hours, minutes] = wallClock;
      const [year, month, day] = datePart.split('-').map(Number);
      return `${day} ${MONTHS[month - 1]} ${year}, ${hours}:${minutes}`;
    }
  }

  // Epoch milliseconds, or a value with no readable wall-clock to preserve.
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default formatEventDateTime;
